// src/components/groups/AcceptInvitation.jsx
// The personal invitation page, #/invite/<token> (Roadmap #54). Shows the group (name, place, logo),
// who invited them and until when; signed out → sign in or create an account (the invitation rides
// the confirmation link, Build 2 §1); signed in → one Join press (the press is the consent, design
// note §3). Every answer is a translated code (Invitations Build 2 Phase 3), never server English.
// The group's place: Architect ruling of 4 Oct 2026 on the Phase 3 questions (Q2).
// Where it lands (addendum 4 Q1a): a private group's invitation always opens its Introduction page
// (LandingPageGroup, "arrived with an invitation" mode, mounted here so the token stays in this
// route); a public group's does so only when the group has written an Introduction.

import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { groupService } from '../../data/dataService';
import StandardButton from '../core/StandardButton';
import InvitationPageShell, {
  InvitationGroupBlock, InvitationNote, InvitationActions, InvitationText
} from './InvitationPageShell';
import InvitationOutcome, { goHome } from './InvitationOutcome';
import LandingPageGroup from './LandingPageGroup';

const AcceptInvitation = ({ inviteToken, currentUser, onShowLogin }) => {
  const { t, formattingLocale } = useTranslation('groups');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);   // a reason code: the invitation can't be used
  const [accepting, setAccepting] = useState(false);
  const [result, setResult] = useState(null);     // { code, groupId, groupName } after a press

  const userId = currentUser?.id || null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setProblem(null);
      setResult(null);
      const data = inviteToken ? await groupService.getInvitationByToken(inviteToken) : null;
      if (cancelled) return;
      if (!data) setProblem('not_found');
      else if (data.error) setProblem('failed');
      else if (data.status === 'accepted') setProblem('already_used');
      else if (data.status === 'revoked') setProblem('revoked');
      else if (data.status === 'expired' || (data.expires_at && new Date(data.expires_at) < new Date())) setProblem('expired');
      setInvitation(data);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [inviteToken]);

  const groupName = invitation?.community?.name || null;

  const handleAccept = async () => {
    if (!userId) {
      onShowLogin?.(t('join_sign_in_prompt'));
      return;
    }
    setAccepting(true);
    const res = await groupService.acceptInvitation(inviteToken, userId);
    setAccepting(false);
    setResult({ code: res.code, groupId: res.groupId, groupName: res.groupName || groupName });
  };

  if (loading) return <InvitationPageShell loading />;

  if (problem) {
    return <InvitationOutcome kind="invite" code={problem} groupName={groupName} />;
  }
  if (result) {
    return <InvitationOutcome kind="invite" code={result.code} groupId={result.groupId} groupName={result.groupName} />;
  }

  const intro = invitation?.introduction;
  if (intro?.found && (intro.accessType !== 'public' || intro.hasIntroduction)) {
    return (
      <LandingPageGroup
        group={{ id: intro.id, name: intro.name }}
        currentUser={currentUser}
        invitation={{ kind: 'invite', token: inviteToken }}
        onShowLogin={onShowLogin}
      />
    );
  }

  const expiry = invitation?.expires_at
    ? new Date(invitation.expires_at).toLocaleDateString(formattingLocale, { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const inviter = invitation?.inviter?.display_name || null;

  return (
    <InvitationPageShell title={groupName ? t('invite_page_title', { group: groupName }) : t('invite_page_title_no_name')}>
      {groupName ? (
        <InvitationGroupBlock
          name={groupName}
          logoUrl={invitation.community.logo_url}
          place={invitation.place}
          description={invitation.community.description}
        />
      ) : (
        // A private group's details are visible to its members only, so before joining all the
        // page knows is that an invitation exists (escalated,
        // docs/escalations/2026-10-04-invitations-phase3-screens-questions.md).
        <InvitationText>{t('invite_page_private_note')}</InvitationText>
      )}
      {inviter && <InvitationNote>{t('invite_page_invited_by', { inviter })}</InvitationNote>}
      {invitation?.invitedEmail && <InvitationNote>{t('invited_as_email', { email: invitation.invitedEmail })}</InvitationNote>}
      {expiry && <InvitationNote icon={<Clock size={14} />}>{t('invite_page_expires', { date: expiry })}</InvitationNote>}

      <InvitationActions>
        {userId ? (
          <StandardButton
            variant="action"
            iconName="Users"
            label={accepting ? t('join_joining') : t('join_button')}
            onClick={handleAccept}
            disabled={accepting}
          />
        ) : (
          <>
            <StandardButton
              variant="action"
              iconName="LogIn"
              label={t('join_sign_in')}
              onClick={() => onShowLogin?.(t('join_sign_in_prompt'))}
            />
            <StandardButton
              variant="ghost"
              iconName="User"
              label={t('join_register')}
              onClick={() => onShowLogin?.(t('join_sign_in_prompt'), 'register')}
            />
          </>
        )}
        <StandardButton variant="ghost" label={t('join_not_now')} onClick={goHome} />
      </InvitationActions>
    </InvitationPageShell>
  );
};

export default AcceptInvitation;
